import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './MediaPage.css';

// EMBY Configuration
const EMBY_CONFIG = {
  serverUrl: 'https://media.allstarmediapro.com',
  apiKey: '8cf23d5e47174b8bae3b58e5eb3e6718',
};

// TMDB Configuration (free API for movie metadata)
const TMDB_CONFIG = {
  apiKey: '2a15ecc7f92a4238d3cb4b1cc8e90143', // Free TMDB API key
  baseUrl: 'https://api.themoviedb.org/3',
  imageBaseUrl: 'https://image.tmdb.org/t/p',
};

// Cache for TMDB lookups to avoid repeated API calls
const tmdbCache: Record<string, any> = {};

// Search TMDB for a movie by title
const searchTMDB = async (title: string, year?: number): Promise<any | null> => {
  const cacheKey = `${title}-${year || ''}`;
  if (tmdbCache[cacheKey]) return tmdbCache[cacheKey];

  try {
    const yearParam = year ? `&year=${year}` : '';
    const searchUrl = `${TMDB_CONFIG.baseUrl}/search/movie?api_key=${TMDB_CONFIG.apiKey}&query=${encodeURIComponent(title)}${yearParam}`;
    const res = await fetch(searchUrl);
    if (res.ok) {
      const data = await res.json();
      if (data.results && data.results.length > 0) {
        const movie = data.results[0];
        // Fetch full details including credits
        const detailsUrl = `${TMDB_CONFIG.baseUrl}/movie/${movie.id}?api_key=${TMDB_CONFIG.apiKey}&append_to_response=credits`;
        const detailsRes = await fetch(detailsUrl);
        if (detailsRes.ok) {
          const details = await detailsRes.json();
          tmdbCache[cacheKey] = details;
          return details;
        }
        tmdbCache[cacheKey] = movie;
        return movie;
      }
    }
  } catch (err) {
    console.error('TMDB search error:', err);
  }
  return null;
};

// Get TMDB image URL
const getTMDBImage = (path: string | null, size: 'w200' | 'w400' | 'w500' | 'w780' | 'original' = 'w400'): string => {
  if (!path) return '';
  return `${TMDB_CONFIG.imageBaseUrl}/${size}${path}`;
};

// Enrich MediaItem with TMDB data
const enrichWithTMDB = async (item: MediaItem): Promise<MediaItem> => {
  if (item.rating && item.cast && item.cast.length > 0) {
    return item; // Already has full metadata with cast photos
  }

  const tmdbData = await searchTMDB(item.title, item.year);
  if (!tmdbData) return item;

  // Get full cast with photos (top 10 actors)
  const castMembers: CastMember[] = (tmdbData.credits?.cast?.slice(0, 10) || []).map((c: any) => ({
    name: c.name,
    character: c.character,
    profilePath: c.profile_path,
  }));

  const actorNames = castMembers.map(c => c.name);
  const director = tmdbData.credits?.crew?.find((c: any) => c.job === 'Director')?.name;

  return {
    ...item,
    rating: item.rating || (tmdbData.vote_average ? Math.round(tmdbData.vote_average * 10) / 10 : undefined),
    synopsis: item.synopsis || tmdbData.overview,
    genres: item.genres.length > 0 ? item.genres : (tmdbData.genres?.map((g: any) => g.name) || []),
    actors: item.actors && item.actors.length > 0 ? item.actors : actorNames,
    cast: castMembers,
    director: item.director || director,
    year: item.year || (tmdbData.release_date ? parseInt(tmdbData.release_date.split('-')[0]) : undefined),
    imageUrl: item.imageUrl || getTMDBImage(tmdbData.poster_path, 'w400'),
    backdropUrl: item.backdropUrl || getTMDBImage(tmdbData.backdrop_path, 'w780'),
    tagline: item.tagline || tmdbData.tagline,
    tmdbId: item.tmdbId || tmdbData.id?.toString(),
  };
};

// Types
interface CastMember {
  name: string;
  character?: string;
  profilePath?: string;
}

interface MediaVersion {
  id: string;
  label: string;
  quality?: string;
  codec?: string;
  language?: string;
}

interface MediaItem {
  id: string;
  title: string;
  year?: number;
  rating?: number;
  criticRating?: number; // Rotten Tomatoes
  runtime?: string;
  genres: string[];
  synopsis?: string;
  imageUrl?: string;
  backdropUrl?: string;
  progress?: number;
  episode?: string;
  type: 'movie' | 'series';
  actors?: string[];
  cast?: CastMember[]; // Full cast with photos
  director?: string;
  studio?: string;
  imdbId?: string;
  tmdbId?: string;
  officialRating?: string; // PG, R, etc
  tagline?: string;
  versions?: MediaVersion[];
  originalTitle?: string; // Used for grouping
}

// Icons
const Icons = {
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  discover: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  watchlist: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  downloads: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  play: <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

// Helper: Get EMBY image URL
const getEmbyImage = (itemId: string, imageTag: string | undefined, type: 'Primary' | 'Backdrop' = 'Primary', width = 400): string => {
  if (!imageTag) return '';
  return `${EMBY_CONFIG.serverUrl}/emby/Items/${itemId}/Images/${type}?maxWidth=${width}&tag=${imageTag}&api_key=${EMBY_CONFIG.apiKey}`;
};

// Helper: Get Emby person image URL
const getEmbyPersonImage = (personId: string, imageTag: string | undefined): string => {
  if (!imageTag) return '';
  return `${EMBY_CONFIG.serverUrl}/emby/Items/${personId}/Images/Primary?maxWidth=200&tag=${imageTag}&api_key=${EMBY_CONFIG.apiKey}`;
};

// Helper: Convert EMBY item to MediaItem
const embyToMediaItem = (item: any): MediaItem => {
  const imageTag = item.ImageTags?.Primary;
  const backdropTag = item.BackdropImageTags?.[0];

  // Extract cast with photos from Emby People data
  const actorPeople = item.People?.filter((p: any) => p.Type === 'Actor').slice(0, 10) || [];
  const actors = actorPeople.map((p: any) => p.Name);
  const cast: CastMember[] = actorPeople.map((p: any) => ({
    name: p.Name,
    character: p.Role,
    profilePath: p.PrimaryImageTag ? getEmbyPersonImage(p.Id, p.PrimaryImageTag) : undefined,
  }));

  const director = item.People?.find((p: any) => p.Type === 'Director')?.Name;

  // Extract title - remove prefix patterns like "01 Movie Name - Genre"
  let title = item.Name || 'Unknown';
  const prefixMatch = title.match(/^\d+\s+(.+?)(?:\s+-\s+\w+)?$/);
  if (prefixMatch) title = prefixMatch[1];

  return {
    id: item.Id,
    title,
    year: item.ProductionYear,
    rating: item.CommunityRating ? Math.round(item.CommunityRating * 10) / 10 : undefined,
    criticRating: item.CriticRating, // Rotten Tomatoes score
    runtime: item.RunTimeTicks ? `${Math.round(item.RunTimeTicks / 600000000)}m` : undefined,
    genres: item.Genres || [],
    synopsis: item.Overview || item.Taglines?.[0],
    imageUrl: imageTag ? getEmbyImage(item.Id, imageTag, 'Primary', 400) : undefined,
    backdropUrl: backdropTag ? getEmbyImage(item.Id, backdropTag, 'Backdrop', 1920) : undefined,
    progress: item.UserData?.PlayedPercentage || 0,
    type: item.Type === 'Movie' ? 'movie' : 'series',
    actors,
    cast,
    director,
    studio: item.Studios?.[0]?.Name,
    imdbId: item.ProviderIds?.Imdb,
    tmdbId: item.ProviderIds?.Tmdb,
    officialRating: item.OfficialRating,
    tagline: item.Taglines?.[0],
    originalTitle: item.Name, // Keep original for version detection
  };
};

// Extract version info from title (e.g., "Movie Name (4K)", "Movie Name - Director's Cut")
const extractVersionInfo = (title: string): { baseTitle: string; versionLabel: string } => {
  // Common version patterns
  const versionPatterns = [
    /\s*[\(\[]?(4K|UHD|2160p)[\)\]]?\s*$/i,
    /\s*[\(\[]?(1080p|HD|720p|BluRay|BRRip)[\)\]]?\s*$/i,
    /\s*[\(\[]?(Director'?s?\s*Cut|Extended|Unrated|Theatrical)[\)\]]?\s*$/i,
    /\s*[\(\[]?(IMAX|3D|Remastered|Special\s*Edition)[\)\]]?\s*$/i,
    /\s*[\(\[]?(Dubbed|Subbed|English|Spanish)[\)\]]?\s*$/i,
    /\s*-\s*(4K|UHD|2160p|1080p|HD|Director'?s?\s*Cut|Extended|IMAX|Remastered)\s*$/i,
  ];

  let baseTitle = title;
  let versionLabel = 'Original';

  for (const pattern of versionPatterns) {
    const match = title.match(pattern);
    if (match) {
      baseTitle = title.replace(pattern, '').trim();
      versionLabel = match[1];
      break;
    }
  }

  // Clean up base title
  baseTitle = baseTitle.replace(/\s+/g, ' ').trim();

  return { baseTitle, versionLabel };
};

// Normalize title for grouping (removes year, punctuation, etc.)
const normalizeForGrouping = (title: string, year?: number): string => {
  let normalized = title.toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ')
    .trim();

  // Include year to avoid grouping different movies with same name
  if (year) normalized += `-${year}`;

  return normalized;
};

// Group items by title, combining versions
const groupMediaVersions = (items: MediaItem[]): MediaItem[] => {
  const grouped = new Map<string, MediaItem>();

  for (const item of items) {
    const { baseTitle, versionLabel } = extractVersionInfo(item.title);
    const groupKey = normalizeForGrouping(baseTitle, item.year);

    if (grouped.has(groupKey)) {
      // Add as a version to existing item
      const existing = grouped.get(groupKey)!;
      if (!existing.versions) {
        existing.versions = [{
          id: existing.id,
          label: extractVersionInfo(existing.title).versionLabel,
          quality: inferQuality(existing.title),
        }];
      }
      existing.versions.push({
        id: item.id,
        label: versionLabel,
        quality: inferQuality(item.title),
      });
    } else {
      // First occurrence - create new grouped item
      const newItem = {
        ...item,
        title: baseTitle, // Use clean base title
        versions: [{
          id: item.id,
          label: versionLabel,
          quality: inferQuality(item.title),
        }],
      };
      grouped.set(groupKey, newItem);
    }
  }

  return Array.from(grouped.values());
};

// Infer quality from title
const inferQuality = (title: string): string => {
  if (/4k|uhd|2160/i.test(title)) return '4K';
  if (/1080|fullhd/i.test(title)) return '1080p';
  if (/720/i.test(title)) return '720p';
  if (/bluray|brrip/i.test(title)) return 'BluRay';
  return 'SD';
};

// Expanded genre list - comprehensive coverage
const ALL_GENRES = [
  'All', 'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
  'Drama', 'Family', 'Fantasy', 'History', 'Horror', 'Music', 'Mystery',
  'Romance', 'Sci-Fi', 'Science Fiction', 'Thriller', 'War', 'Western'
];

// Duration filter options (in minutes)
const DURATION_FILTERS = [
  { label: 'Any Length', min: 0, max: Infinity },
  { label: 'Under 90 min', min: 0, max: 90 },
  { label: '90-120 min', min: 90, max: 120 },
  { label: '120-150 min', min: 120, max: 150 },
  { label: 'Over 150 min', min: 150, max: Infinity },
  { label: 'Under 30 min', min: 0, max: 30 },
];

// Year/Decade filter options
const YEAR_FILTERS = [
  { label: 'Any Year', min: 0, max: 9999 },
  { label: '2020s', min: 2020, max: 2029 },
  { label: '2010s', min: 2010, max: 2019 },
  { label: '2000s', min: 2000, max: 2009 },
  { label: '1990s', min: 1990, max: 1999 },
  { label: '1980s', min: 1980, max: 1989 },
  { label: 'Pre-1980', min: 0, max: 1979 },
];

// Rating filter (MPAA/TV ratings)
const RATING_FILTERS = [
  { label: 'Any Rating', values: [] as string[] },
  { label: 'G / TV-G', values: ['G', 'TV-G'] },
  { label: 'PG / TV-PG', values: ['PG', 'TV-PG'] },
  { label: 'PG-13 / TV-14', values: ['PG-13', 'TV-14'] },
  { label: 'R / TV-MA', values: ['R', 'TV-MA', 'NC-17'] },
  { label: 'Unrated', values: ['NR', 'Unrated', ''] },
];

// Sort options
const SORT_OPTIONS = [
  { label: 'Recently Added', key: 'dateAdded', order: 'desc' as const },
  { label: 'Title A-Z', key: 'title', order: 'asc' as const },
  { label: 'Title Z-A', key: 'title', order: 'desc' as const },
  { label: 'Year (Newest)', key: 'year', order: 'desc' as const },
  { label: 'Year (Oldest)', key: 'year', order: 'asc' as const },
  { label: 'Rating (Highest)', key: 'rating', order: 'desc' as const },
  { label: 'Rating (Lowest)', key: 'rating', order: 'asc' as const },
];

// Filter state interface
interface FilterState {
  genres: string[];
  duration: typeof DURATION_FILTERS[0];
  year: typeof YEAR_FILTERS[0];
  rating: typeof RATING_FILTERS[0];
  contentType: 'all' | 'movie' | 'series';
  minRating: number;
  actors: string[];
  sort: typeof SORT_OPTIONS[0];
}

const defaultFilters: FilterState = {
  genres: [],
  duration: DURATION_FILTERS[0],
  year: YEAR_FILTERS[0],
  rating: RATING_FILTERS[0],
  contentType: 'all',
  minRating: 0,
  actors: [],
  sort: SORT_OPTIONS[0],
};

// Helper to extract runtime in minutes
const getRuntimeMinutes = (runtime: string | undefined): number => {
  if (!runtime) return 0;
  const match = runtime.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Filter icons
const FilterIcons = {
  filter: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  star: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  film: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>,
  sort: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="8" y2="18"/></svg>,
  chevronDown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  reset: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38"/></svg>,
};

// ========== COMPONENTS ==========

// Hamburger Menu Icon
const HamburgerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Sidebar: React.FC<{ activeNav: string; onNavChange: (id: string) => void; isCollapsed: boolean; onToggle: () => void }> = ({ activeNav, onNavChange, isCollapsed, onToggle }) => (
  <>
    {/* Hamburger Button - Always Visible */}
    <button
      className={`sidebar-hamburger ${isCollapsed ? '' : 'sidebar-hamburger--open'}`}
      onClick={onToggle}
      aria-label={isCollapsed ? 'Open menu' : 'Close menu'}
    >
      {isCollapsed ? <HamburgerIcon /> : <CloseIcon />}
    </button>

    {/* Overlay */}
    {!isCollapsed && <div className="sidebar-overlay" onClick={onToggle} />}

    {/* Sidebar */}
    <aside className={`media-sidebar ${isCollapsed ? 'media-sidebar--collapsed' : ''}`}>
      <div className="media-sidebar__header">
        <div className="media-sidebar__logo">
          <span className="media-sidebar__logo-icon">★</span>
          <span className="media-sidebar__logo-text">Media Pro</span>
        </div>
        <button className="media-sidebar__close" onClick={onToggle}>
          <CloseIcon />
        </button>
      </div>

      <nav className="media-sidebar__nav">
        {[
          { id: 'home', label: 'Home', icon: Icons.home },
          { id: 'discover', label: 'Discover', icon: Icons.discover },
          { id: 'watchlist', label: 'Watchlist', icon: Icons.watchlist },
          { id: 'downloads', label: 'Downloads', icon: Icons.downloads },
        ].map(item => (
          <button
            key={item.id}
            className={`media-sidebar__item ${activeNav === item.id ? 'media-sidebar__item--active' : ''}`}
            onClick={() => { onNavChange(item.id); onToggle(); }}
          >
            <span className="media-sidebar__item-icon">{item.icon}</span>
            <span className="media-sidebar__item-label">{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="media-sidebar__footer">
        <button className="media-sidebar__item">
          <span className="media-sidebar__item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </span>
          <span className="media-sidebar__item-label">Settings</span>
        </button>
      </div>
    </aside>
  </>
);

// Advanced Filter Panel Component
const FilterPanel: React.FC<{
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  allActors: string[];
  onReset: () => void;
  activeFiltersCount: number;
}> = ({ filters, onFiltersChange, allActors, onReset, activeFiltersCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [actorSearch, setActorSearch] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const filteredActors = actorSearch
    ? allActors.filter(a => a.toLowerCase().includes(actorSearch.toLowerCase())).slice(0, 20)
    : allActors.slice(0, 20);

  const toggleGenre = (genre: string) => {
    const newGenres = filters.genres.includes(genre)
      ? filters.genres.filter(g => g !== genre)
      : [...filters.genres, genre];
    onFiltersChange({ ...filters, genres: newGenres });
  };

  return (
    <div className="filter-panel">
      <div className="filter-panel__header">
        <button className={`filter-panel__toggle ${isOpen ? 'active' : ''} ${activeFiltersCount > 0 ? 'has-filters' : ''}`} onClick={() => setIsOpen(!isOpen)}>
          {FilterIcons.filter}
          <span>Filters</span>
          {activeFiltersCount > 0 && <span className="filter-panel__badge">{activeFiltersCount}</span>}
          {FilterIcons.chevronDown}
        </button>
        {activeFiltersCount > 0 && (
          <button className="filter-panel__reset" onClick={onReset} title="Reset all filters">
            {FilterIcons.reset}
            <span>Reset</span>
          </button>
        )}
      </div>

      {isOpen && (
        <div className="filter-panel__content">
          {/* Genre Multi-Select */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.film} Genres</div>
            <div className="filter-chips">
              {ALL_GENRES.filter(g => g !== 'All').map(genre => (
                <button
                  key={genre}
                  className={`filter-chip ${filters.genres.includes(genre) ? 'active' : ''}`}
                  onClick={() => toggleGenre(genre)}
                >
                  {filters.genres.includes(genre) && FilterIcons.check}
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Content Type */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.film} Content Type</div>
            <div className="filter-buttons">
              {(['all', 'movie', 'series'] as const).map(type => (
                <button
                  key={type}
                  className={`filter-btn ${filters.contentType === type ? 'active' : ''}`}
                  onClick={() => onFiltersChange({ ...filters, contentType: type })}
                >
                  {type === 'all' ? 'All' : type === 'movie' ? 'Movies' : 'TV Shows'}
                </button>
              ))}
            </div>
          </div>

          {/* Duration Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.clock} Duration</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'duration' ? null : 'duration')}>
              <span>{filters.duration.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'duration' && (
                <div className="filter-dropdown__menu">
                  {DURATION_FILTERS.map((opt, i) => (
                    <button key={i} className={filters.duration.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, duration: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Year Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.calendar} Release Year</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'year' ? null : 'year')}>
              <span>{filters.year.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'year' && (
                <div className="filter-dropdown__menu">
                  {YEAR_FILTERS.map((opt, i) => (
                    <button key={i} className={filters.year.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, year: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* MPAA Rating Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.star} Age Rating</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'rating' ? null : 'rating')}>
              <span>{filters.rating.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'rating' && (
                <div className="filter-dropdown__menu">
                  {RATING_FILTERS.map((opt, i) => (
                    <button key={i} className={filters.rating.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, rating: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Minimum Score Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.star} Minimum Score</div>
            <div className="filter-slider">
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={filters.minRating}
                onChange={(e) => onFiltersChange({ ...filters, minRating: parseFloat(e.target.value) })}
              />
              <span className="filter-slider__value">{filters.minRating > 0 ? `${filters.minRating}+` : 'Any'}</span>
            </div>
          </div>

          {/* Actor Filter */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.user} Actors</div>
            <div className="filter-actor-search">
              <input
                type="text"
                placeholder="Search actors..."
                value={actorSearch}
                onChange={(e) => setActorSearch(e.target.value)}
              />
              {filters.actors.length > 0 && (
                <div className="filter-actor-selected">
                  {filters.actors.map(actor => (
                    <span key={actor} className="filter-actor-tag">
                      {actor}
                      <button onClick={() => onFiltersChange({ ...filters, actors: filters.actors.filter(a => a !== actor) })}>×</button>
                    </span>
                  ))}
                </div>
              )}
              {actorSearch && (
                <div className="filter-actor-suggestions">
                  {filteredActors.map(actor => (
                    <button
                      key={actor}
                      className={filters.actors.includes(actor) ? 'active' : ''}
                      onClick={() => {
                        if (!filters.actors.includes(actor)) {
                          onFiltersChange({ ...filters, actors: [...filters.actors, actor] });
                        }
                        setActorSearch('');
                      }}
                    >
                      {actor}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sort Options */}
          <div className="filter-group">
            <div className="filter-group__label">{FilterIcons.sort} Sort By</div>
            <div className="filter-dropdown" onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')}>
              <span>{filters.sort.label}</span>
              {FilterIcons.chevronDown}
              {openDropdown === 'sort' && (
                <div className="filter-dropdown__menu">
                  {SORT_OPTIONS.map((opt, i) => (
                    <button key={i} className={filters.sort.label === opt.label ? 'active' : ''} onClick={(e) => { e.stopPropagation(); onFiltersChange({ ...filters, sort: opt }); setOpenDropdown(null); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Header with genre quick-filters
const Header: React.FC<{
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onSearchClick: () => void;
  allActors: string[];
  onResetFilters: () => void;
  activeFiltersCount: number;
}> = ({ filters, onFiltersChange, onSearchClick, allActors, onResetFilters, activeFiltersCount }) => {
  // Quick genre selection (first 8 genres for the header bar)
  const quickGenres = ALL_GENRES.slice(0, 10);
  const selectedQuickGenre = filters.genres.length === 1 ? filters.genres[0] : (filters.genres.length === 0 ? 'All' : 'Multi');

  const handleQuickGenreClick = (genre: string) => {
    if (genre === 'All') {
      onFiltersChange({ ...filters, genres: [] });
    } else {
      onFiltersChange({ ...filters, genres: [genre] });
    }
  };

  return (
    <header className="orama-header">
      <div className="orama-genres">
        {quickGenres.map(genre => (
          <button
            key={genre}
            className={`orama-genre-btn${(genre === 'All' && filters.genres.length === 0) || filters.genres.includes(genre) ? ' orama-genre-btn--active' : ''}`}
            onClick={() => handleQuickGenreClick(genre)}
          >
            {((genre === 'All' && filters.genres.length === 0) || (filters.genres.length === 1 && filters.genres[0] === genre)) && <span className="orama-genre-indicator">▶</span>}
            {genre}
          </button>
        ))}
      </div>
      <div className="orama-header__right">
        <button className="orama-search-btn" onClick={onSearchClick}>
          {Icons.search}
          <span>Search</span>
        </button>
        <div className="orama-avatar">JW</div>
      </div>
    </header>
  );
};

const Hero: React.FC<{ item: MediaItem | null; onPlay: () => void; onAddWatchlist: () => void }> = ({ item, onPlay, onAddWatchlist }) => {
  if (!item) return <div className="orama-hero orama-hero--loading"><div className="orama-spinner" /></div>;

  return (
    <div className="orama-hero">
      <div className="orama-hero__bg" style={{ backgroundImage: item.backdropUrl || item.imageUrl ? `url(${item.backdropUrl || item.imageUrl})` : undefined }} />
      <div className="orama-hero__content">
        <h1 className="orama-hero__title">{item.title}</h1>
        <div className="orama-hero__meta">
          {item.genres[0] && <span>{item.genres[0]}</span>}
          {item.year && <span> • {item.year}</span>}
          {item.rating && <span> • ★ {item.rating}</span>}
        </div>
        <p className="orama-hero__desc">{item.synopsis ? item.synopsis.slice(0, 180) + '...' : 'A gripping new series.'}</p>
        <div className="orama-hero__actions">
          <button className="orama-btn orama-btn--primary" onClick={onPlay}>{Icons.play} Watch Now</button>
          <button className="orama-btn orama-btn--secondary" onClick={onAddWatchlist}>{Icons.plus} Add to Watchlist</button>
        </div>
      </div>
    </div>
  );
};

const ContinueCard: React.FC<{ item: MediaItem; onClick: () => void }> = ({ item, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  // Use item progress from EMBY or calculate stable progress based on item ID
  const progress = item.progress || ((parseInt(item.id.replace(/\D/g, '').slice(-2) || '50', 10) % 80) + 15);
  // Generate stable values based on item ID hash
  const idNum = parseInt(item.id.replace(/\D/g, '').slice(-4) || '1234', 10);
  const season = ((idNum % 3) + 1);
  const minutes = (idNum % 120) + 10;
  const seconds = (idNum % 60);

  return (
    <div className="orama-continue-card" onClick={onClick}>
      <div className="orama-continue-card__poster">
        {item.backdropUrl || item.imageUrl ? (
          <img src={item.backdropUrl || item.imageUrl} alt={item.title} loading="lazy" onLoad={() => setLoaded(true)} className={loaded ? 'loaded' : ''} />
        ) : <div className="orama-card-placeholder">🎬</div>}
      </div>
      <div className="orama-continue-card__info">
        <div className="orama-continue-card__title-row">
          <span className="orama-continue-card__title">{item.title}</span>
          <span className="orama-continue-card__episode">{item.type === 'series' ? `S${season}` : ''}</span>
          <span className="orama-continue-card__time">{minutes}:{seconds.toString().padStart(2, '0')}</span>
        </div>
        <div className="orama-continue-card__progress"><div className="orama-continue-card__progress-bar" style={{ width: `${progress}%` }} /></div>
      </div>
    </div>
  );
};

const TrendingCard: React.FC<{ item: MediaItem; onClick: () => void }> = ({ item, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="orama-trending-card" onClick={onClick} data-title={item.title}>
      <div className="orama-trending-card__poster">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.title} loading="lazy" onLoad={() => setLoaded(true)} className={loaded ? 'loaded' : ''} />
        ) : <div className="orama-card-placeholder">🎬</div>}
        <div className="orama-trending-card__overlay"><div className="orama-trending-card__play">{Icons.play}</div></div>
      </div>
      <h3 className="orama-trending-card__title">{item.title}</h3>
      <div className="orama-trending-card__meta">
        {item.rating && <span className="orama-trending-card__rating" title="IMDB">★ {item.rating}</span>}
        {item.criticRating && <span className="orama-trending-card__critic" title="Rotten Tomatoes">🍅 {item.criticRating}%</span>}
        {item.genres[0] && <span className="orama-trending-card__genre">{item.genres[0]}</span>}
      </div>
    </div>
  );
};

const ContentSection: React.FC<{
  title: string;
  count?: number;
  items: MediaItem[];
  onItemClick: (item: MediaItem) => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
}> = ({ title, count, items, onItemClick, expanded, onToggleExpand }) => {
  const displayItems = expanded ? items : items.slice(0, 15);
  const hasMore = items.length > 15;

  const handleSeeAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`[ContentSection] "${title}" See All clicked, expanded=${expanded}, hasMore=${hasMore}, itemCount=${items.length}`);
    onToggleExpand?.();
  };

  return (
    <section className="orama-section">
      <div className="orama-section__header">
        <h2 className="orama-section__title">
          {title}
          <span className="orama-section__count"> ({items.length} items{expanded ? ' - EXPANDED' : ''})</span>
        </h2>
        {hasMore && (
          <button className="orama-section__more" onClick={handleSeeAll}>
            {expanded ? '← Show less' : `See all ${items.length} →`}
          </button>
        )}
      </div>
      <div className={`orama-row ${expanded ? 'orama-row--expanded' : ''}`}>
        {displayItems.map(item => <TrendingCard key={item.id} item={item} onClick={() => onItemClick(item)} />)}
      </div>
      {expanded && (
        <div className="orama-section__load-info">Showing all {items.length} items</div>
      )}
    </section>
  );
};

const SearchOverlay: React.FC<{ isOpen: boolean; onClose: () => void; results: MediaItem[]; onSearch: (q: string) => void; onItemClick: (i: MediaItem) => void }> = ({ isOpen, onClose, results, onSearch, onItemClick }) => {
  const [query, setQuery] = useState('');
  useEffect(() => { if (isOpen && query.length > 1) { const t = setTimeout(() => onSearch(query), 300); return () => clearTimeout(t); } }, [query, isOpen, onSearch]);
  if (!isOpen) return null;
  return (
    <div className="orama-search-overlay">
      <button className="orama-search-overlay__close" onClick={onClose}>{Icons.x}</button>
      <input type="text" className="orama-search-input" placeholder="Search movies, shows..." value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      <div className="orama-search-results">
        <div className="orama-search-grid">{results.map(item => <TrendingCard key={item.id} item={item} onClick={() => { onClose(); onItemClick(item); }} />)}</div>
        {query.length > 1 && results.length === 0 && <div className="orama-empty">No results for "{query}"</div>}
      </div>
    </div>
  );
};

const DetailModal: React.FC<{ item: MediaItem | null; onClose: () => void; onPlay: (versionId?: string) => void; similarItems: MediaItem[]; onSelectSimilar: (item: MediaItem) => void; onActorClick: (actorName: string) => void }> = ({ item, onClose, onPlay, similarItems, onSelectSimilar, onActorClick }) => {
  const [enrichedItem, setEnrichedItem] = useState<MediaItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  useEffect(() => {
    if (!item) {
      setEnrichedItem(null);
      setSelectedVersionId(null);
      return;
    }
    // Set default version to the first/best quality one
    if (item.versions && item.versions.length > 0) {
      // Prefer 4K, then 1080p, then first available
      const fourK = item.versions.find(v => v.quality === '4K');
      const hd = item.versions.find(v => v.quality === '1080p');
      setSelectedVersionId(fourK?.id || hd?.id || item.versions[0].id);
    }
    // If item lacks metadata, fetch from TMDB
    if (!item.rating || !item.actors || item.actors.length === 0) {
      setLoading(true);
      enrichWithTMDB(item).then(enriched => {
        setEnrichedItem(enriched);
        setLoading(false);
      });
    } else {
      setEnrichedItem(item);
    }
  }, [item]);

  if (!item) return null;
  const displayItem = enrichedItem || item;

  return (
    <div className="orama-detail-modal" onClick={onClose}>
      <div className="orama-detail-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="orama-detail-modal__header">
          {(displayItem.backdropUrl || displayItem.imageUrl) && <img src={displayItem.backdropUrl || displayItem.imageUrl} alt={displayItem.title} />}
          <button className="orama-detail-modal__close" onClick={onClose}>{Icons.x}</button>
          {loading && <div className="orama-detail-modal__loading">Loading details...</div>}
        </div>
        <div className="orama-detail-modal__body">
          <h1 className="orama-detail-modal__title">{displayItem.title}</h1>
          <div className="orama-detail-modal__meta">
            {displayItem.rating && <span className="orama-detail-modal__rating" title="TMDB Rating">★ {displayItem.rating}</span>}
            {displayItem.criticRating && <span className="orama-detail-modal__critic" title="Rotten Tomatoes">🍅 {displayItem.criticRating}%</span>}
            {displayItem.officialRating && <span className="orama-detail-modal__mpaa">{displayItem.officialRating}</span>}
            {displayItem.year && <span>{displayItem.year}</span>}
            {displayItem.runtime && <span>{displayItem.runtime}</span>}
            {displayItem.genres.slice(0, 2).map(g => <span key={g} className="orama-detail-modal__genre">{g}</span>)}
          </div>
          {displayItem.tagline && <p className="orama-detail-modal__tagline">"{displayItem.tagline}"</p>}
          <p className="orama-detail-modal__desc">{displayItem.synopsis || 'No description available.'}</p>

          {/* Cast & Crew */}
          <div className="media-detail__credits">
            {displayItem.director && <p className="media-detail__director"><strong>Director:</strong> {displayItem.director}</p>}
            {displayItem.studio && <p className="media-detail__studio"><strong>Studio:</strong> {displayItem.studio}</p>}

            {/* Cast with Photos - Clickable to filter by actor */}
            {displayItem.cast && displayItem.cast.length > 0 && (
              <div className="media-detail__cast">
                <h4 className="media-detail__cast-title">Cast <span className="media-detail__cast-hint">(tap to see their movies)</span></h4>
                <div className="media-detail__cast-grid">
                  {displayItem.cast.slice(0, 8).map((actor, idx) => (
                    <div
                      key={idx}
                      className="media-detail__cast-card media-detail__cast-card--clickable"
                      onClick={() => onActorClick(actor.name)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onActorClick(actor.name)}
                    >
                      <div className="media-detail__cast-avatar">
                        {actor.profilePath ? (
                          <img
                            src={actor.profilePath.startsWith('http') ? actor.profilePath : getTMDBImage(actor.profilePath, 'w200')}
                            alt={actor.name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="media-detail__cast-placeholder">
                            {actor.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="media-detail__cast-info">
                        <span className="media-detail__cast-name">{actor.name}</span>
                        {actor.character && (
                          <span className="media-detail__cast-role">{actor.character}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback: show actor names as text if no photos - also clickable */}
            {(!displayItem.cast || displayItem.cast.length === 0) && displayItem.actors && displayItem.actors.length > 0 && (
              <p className="media-detail__actors-text">
                <strong>Cast:</strong>{' '}
                {displayItem.actors.map((actor, idx) => (
                  <span key={actor}>
                    <span
                      className="media-detail__actor-link"
                      onClick={() => onActorClick(actor)}
                      role="button"
                      tabIndex={0}
                    >
                      {actor}
                    </span>
                    {idx < displayItem.actors!.length - 1 && ', '}
                  </span>
                ))}
              </p>
            )}
          </div>

          {/* Version Selector - only show if multiple versions exist */}
          {displayItem.versions && displayItem.versions.length > 1 && (
            <div className="orama-detail-modal__versions">
              <label className="orama-detail-modal__versions-label">Version:</label>
              <select
                className="orama-detail-modal__versions-select"
                value={selectedVersionId || ''}
                onChange={(e) => setSelectedVersionId(e.target.value)}
              >
                {displayItem.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label} {v.quality && v.quality !== 'SD' ? `• ${v.quality}` : ''} {v.language ? `• ${v.language}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="orama-detail-modal__actions">
            <button className="orama-btn orama-btn--primary" onClick={() => onPlay(selectedVersionId || undefined)}>{Icons.play} Play</button>
            <button className="orama-btn orama-btn--secondary">{Icons.plus} Watchlist</button>
            {displayItem.tmdbId && (
              <a href={`https://www.themoviedb.org/movie/${displayItem.tmdbId}`} target="_blank" rel="noopener noreferrer" className="orama-btn orama-btn--secondary">
                TMDB ↗
              </a>
            )}
            {displayItem.imdbId && (
              <a href={`https://www.imdb.com/title/${displayItem.imdbId}`} target="_blank" rel="noopener noreferrer" className="orama-btn orama-btn--secondary">
                IMDB ↗
              </a>
            )}
          </div>
          {similarItems.length > 0 && <>
            <h3 className="orama-detail-modal__section-title">More Like This</h3>
            <div className="orama-row">{similarItems.slice(0, 6).map(s => <TrendingCard key={s.id} item={s} onClick={() => onSelectSimilar(s)} />)}</div>
          </>}
        </div>
      </div>
    </div>
  );
};

// Advanced Video Player Icons
const PlayerIcons = {
  play: <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  pause: <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  volumeHigh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  volumeLow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  volumeMute: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>,
  fullscreen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>,
  exitFullscreen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>,
  skipBack: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 19 2 12 11 5 11 19"/><polygon points="22 19 13 12 22 5 22 19"/></svg>,
  skipForward: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 19 22 12 13 5 13 19"/><polygon points="2 19 11 12 2 5 2 19"/></svg>,
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  pip: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor"/></svg>,
  subtitles: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="6" y1="16" x2="14" y2="16"/></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>,
};

// Format time helper
const formatPlayerTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Quality options for Emby streaming
const QUALITY_OPTIONS = [
  { label: 'Auto', bitrate: 0 },
  { label: '4K', bitrate: 120000000 },
  { label: '1080p', bitrate: 20000000 },
  { label: '720p', bitrate: 8000000 },
  { label: '480p', bitrate: 3000000 },
  { label: '360p', bitrate: 1500000 },
];

const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

interface SubtitleTrack {
  index: number;
  language: string;
  displayTitle: string;
}

const VideoPlayer: React.FC<{ item: MediaItem | null; onClose: () => void }> = ({ item, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const hideControlsTimer = useRef<NodeJS.Timeout | null>(null);

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [quality, setQuality] = useState(QUALITY_OPTIONS[0]);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'main' | 'quality' | 'speed' | 'subtitles'>('main');
  const [subtitles, setSubtitles] = useState<SubtitleTrack[]>([]);
  const [activeSubtitle, setActiveSubtitle] = useState<number>(-1);
  const [isPiP, setIsPiP] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Store the actual MediaSourceId from PlaybackInfo
  const [mediaSourceId, setMediaSourceId] = useState<string | null>(null);

  // Build stream URL - Use direct file download for maximum compatibility
  const getStreamUrl = useCallback(() => {
    if (!item) return '';
    // Direct file access - bypasses Emby streaming API issues
    // This works for any browser-compatible video format (mp4, webm, etc)
    return `${EMBY_CONFIG.serverUrl}/emby/Items/${item.id}/Download?api_key=${EMBY_CONFIG.apiKey}`;
  }, [item]);

  // Fetch PlaybackInfo for MediaSourceId and subtitles
  useEffect(() => {
    if (!item) return;
    fetch(`${EMBY_CONFIG.serverUrl}/emby/Items/${item.id}/PlaybackInfo?api_key=${EMBY_CONFIG.apiKey}`)
      .then(res => res.json())
      .then(data => {
        const mediaSource = data.MediaSources?.[0];
        if (mediaSource) {
          // Set the actual MediaSourceId from PlaybackInfo
          setMediaSourceId(mediaSource.Id);
          console.log('[VideoPlayer] MediaSource:', mediaSource.Id, mediaSource.Path);

          // Get subtitles
          if (mediaSource.MediaStreams) {
            const subs = mediaSource.MediaStreams
              .filter((s: any) => s.Type === 'Subtitle')
              .map((s: any) => ({
                index: s.Index,
                language: s.Language || 'Unknown',
                displayTitle: s.DisplayTitle || s.Language || 'Subtitle',
              }));
            setSubtitles(subs);
          }
        }
      })
      .catch(err => console.error('[VideoPlayer] PlaybackInfo error:', err));
  }, [item]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!videoRef.current) return;

      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'arrowleft':
          e.preventDefault();
          skip(-10);
          break;
        case 'arrowright':
          e.preventDefault();
          skip(10);
          break;
        case 'arrowup':
          e.preventDefault();
          adjustVolume(0.1);
          break;
        case 'arrowdown':
          e.preventDefault();
          adjustVolume(-0.1);
          break;
        case 'escape':
          if (isFullscreen) {
            document.exitFullscreen();
          } else {
            onClose();
          }
          break;
        case 'p':
          e.preventDefault();
          togglePiP();
          break;
        case 'c':
          e.preventDefault();
          cycleSubtitles();
          break;
        case ',':
          e.preventDefault();
          changeSpeed(-1);
          break;
        case '.':
          e.preventDefault();
          changeSpeed(1);
          break;
        case '0': case '1': case '2': case '3': case '4':
        case '5': case '6': case '7': case '8': case '9':
          e.preventDefault();
          const percent = parseInt(e.key) * 10;
          videoRef.current.currentTime = (duration * percent) / 100;
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [duration, isFullscreen, subtitles, activeSubtitle, playbackSpeed]);

  // Auto-hide controls
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    if (isPlaying) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  // Player control functions
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const adjustVolume = (delta: number) => {
    const newVol = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVol);
    if (videoRef.current) videoRef.current.volume = newVol;
    setIsMuted(newVol === 0);
  };

  const skip = (seconds: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePiP = async () => {
    if (!videoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else {
        await videoRef.current.requestPictureInPicture();
        setIsPiP(true);
      }
    } catch (err) {
      console.error('PiP error:', err);
    }
  };

  const cycleSubtitles = () => {
    if (subtitles.length === 0) return;
    const nextIndex = activeSubtitle === -1 ? 0 : (activeSubtitle + 1 >= subtitles.length ? -1 : activeSubtitle + 1);
    setActiveSubtitle(nextIndex);
  };

  const changeSpeed = (direction: number) => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const newIndex = Math.max(0, Math.min(PLAYBACK_SPEEDS.length - 1, currentIndex + direction));
    setPlaybackSpeed(PLAYBACK_SPEEDS[newIndex]);
    if (videoRef.current) videoRef.current.playbackRate = PLAYBACK_SPEEDS[newIndex];
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !videoRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = duration * percent;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    if (videoRef.current) videoRef.current.volume = newVol;
    setIsMuted(newVol === 0);
  };

  // Video event handlers
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    if (videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    setIsLoading(false);
  };

  if (!item) return null;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={`vp ${showControls ? 'vp--show' : ''} ${isFullscreen ? 'vp--fs' : ''}`}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onClick={(e) => { if (e.target === e.currentTarget) togglePlay(); }}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={getStreamUrl()}
        autoPlay
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        onError={() => setError('Failed to load video')}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Ambient Glow Effect */}
      <div className="vp__ambient" />

      {/* Loading State */}
      {isLoading && (
        <div className="vp__loading">
          <div className="vp__loading-ring">
            <div /><div /><div /><div />
          </div>
          <span>Loading...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="vp__error">
          <div className="vp__error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <p>{error}</p>
          <button className="vp__error-btn" onClick={() => { setError(null); videoRef.current?.load(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Try Again
          </button>
        </div>
      )}

      {/* Top Bar */}
      <div className="vp__top">
        <button className="vp__icon-btn vp__icon-btn--back" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="vp__info">
          <h1>{item.title}</h1>
          {item.year && <span className="vp__year">{item.year}</span>}
          {item.officialRating && <span className="vp__rating">{item.officialRating}</span>}
        </div>
        <div className="vp__top-right">
          {quality.label !== 'Auto' && <span className="vp__quality-badge">{quality.label}</span>}
        </div>
      </div>

      {/* Center Controls */}
      <div className="vp__center">
        <button className="vp__skip-btn vp__skip-btn--back" onClick={() => skip(-10)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 3C17.15 3 21.08 6.03 22.47 10.22L20.1 11C19.05 7.81 16.04 5.5 12.5 5.5C10.54 5.5 8.77 6.22 7.38 7.38L10 10H3V3L5.6 5.6C7.45 4 9.85 3 12.5 3M10 12V22H12V14.85L10 12M6 12V22H8V12H6M14 12.25V22H16V14.45L14 12.25M18 15V22H20V16.5L18 15Z"/></svg>
          <span>10</span>
        </button>

        <button className="vp__play-btn" onClick={togglePlay}>
          <div className="vp__play-btn-bg" />
          <div className="vp__play-btn-icon">
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.14v14l11-7-11-7z"/></svg>
            )}
          </div>
          <div className="vp__play-btn-ring" />
        </button>

        <button className="vp__skip-btn vp__skip-btn--forward" onClick={() => skip(10)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.5 3C6.85 3 2.92 6.03 1.53 10.22L3.9 11C4.95 7.81 7.96 5.5 11.5 5.5C13.46 5.5 15.23 6.22 16.62 7.38L14 10H21V3L18.4 5.6C16.55 4 14.15 3 11.5 3M10 12V22H12V14.85L10 12M6 12V22H8V12H6M14 12.25V22H16V14.45L14 12.25M18 15V22H20V16.5L18 15Z"/></svg>
          <span>10</span>
        </button>
      </div>

      {/* Bottom Controls */}
      <div className="vp__bottom">
        {/* Progress Bar */}
        <div className="vp__progress-wrap">
          <div className="vp__progress" ref={progressRef} onClick={handleSeek}>
            <div className="vp__progress-track" />
            <div className="vp__progress-buffer" style={{ width: `${bufferedPercent}%` }} />
            <div className="vp__progress-fill" style={{ width: `${progressPercent}%` }} />
            <div className="vp__progress-glow" style={{ left: `${progressPercent}%` }} />
            <div className="vp__progress-thumb" style={{ left: `${progressPercent}%` }}>
              <div className="vp__progress-thumb-inner" />
            </div>
          </div>
          <div className="vp__time-labels">
            <span>{formatPlayerTime(currentTime)}</span>
            <span>{formatPlayerTime(duration)}</span>
          </div>
        </div>

        {/* Controls Row */}
        <div className="vp__controls">
          <div className="vp__controls-left">
            {/* Play/Pause */}
            <button className="vp__ctrl-btn" onClick={togglePlay} data-tooltip={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>

            {/* Skip Back */}
            <button className="vp__ctrl-btn" onClick={() => skip(-10)} data-tooltip="-10s">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 19 2 12 11 5 11 19"/><polygon points="22 19 13 12 22 5 22 19"/></svg>
            </button>

            {/* Skip Forward */}
            <button className="vp__ctrl-btn" onClick={() => skip(10)} data-tooltip="+10s">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 19 22 12 13 5 13 19"/><polygon points="2 19 11 12 2 5 2 19"/></svg>
            </button>

            {/* Volume */}
            <div className="vp__volume-group">
              <button className="vp__ctrl-btn" onClick={toggleMute} data-tooltip={isMuted ? 'Unmute' : 'Mute'}>
                {isMuted || volume === 0 ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                ) : volume < 0.5 ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                )}
              </button>
              <div className="vp__volume-slider-wrap">
                <input
                  type="range"
                  className="vp__volume-slider"
                  min="0"
                  max="1"
                  step="0.02"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  style={{ '--volume': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                />
              </div>
            </div>

            {/* Time */}
            <div className="vp__time">
              <span className="vp__time-current">{formatPlayerTime(currentTime)}</span>
              <span className="vp__time-sep">/</span>
              <span className="vp__time-total">{formatPlayerTime(duration)}</span>
            </div>
          </div>

          <div className="vp__controls-right">
            {/* Speed */}
            {playbackSpeed !== 1 && (
              <span className="vp__speed-badge">{playbackSpeed}x</span>
            )}

            {/* Subtitles */}
            {subtitles.length > 0 && (
              <button className={`vp__ctrl-btn ${activeSubtitle >= 0 ? 'vp__ctrl-btn--active' : ''}`} onClick={cycleSubtitles} data-tooltip="Subtitles">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="6" y1="16" x2="14" y2="16"/></svg>
              </button>
            )}

            {/* Settings */}
            <div className="vp__settings-wrap">
              <button className={`vp__ctrl-btn ${showSettings ? 'vp__ctrl-btn--active' : ''}`} onClick={() => setShowSettings(!showSettings)} data-tooltip="Settings">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>

              {showSettings && (
                <div className="vp__settings-panel">
                  <div className="vp__settings-header">
                    {settingsTab !== 'main' && (
                      <button className="vp__settings-back-btn" onClick={() => setSettingsTab('main')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                      </button>
                    )}
                    <span>{settingsTab === 'main' ? 'Settings' : settingsTab === 'speed' ? 'Speed' : settingsTab === 'quality' ? 'Quality' : 'Subtitles'}</span>
                  </div>
                  <div className="vp__settings-body">
                    {settingsTab === 'main' && (
                      <>
                        <button className="vp__settings-item" onClick={() => setSettingsTab('speed')}>
                          <span className="vp__settings-item-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          </span>
                          <span className="vp__settings-item-label">Playback Speed</span>
                          <span className="vp__settings-item-value">{playbackSpeed}x</span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                        <button className="vp__settings-item" onClick={() => setSettingsTab('quality')}>
                          <span className="vp__settings-item-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                          </span>
                          <span className="vp__settings-item-label">Quality</span>
                          <span className="vp__settings-item-value">{quality.label}</span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                        {subtitles.length > 0 && (
                          <button className="vp__settings-item" onClick={() => setSettingsTab('subtitles')}>
                            <span className="vp__settings-item-icon">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="6" y1="16" x2="14" y2="16"/></svg>
                            </span>
                            <span className="vp__settings-item-label">Subtitles</span>
                            <span className="vp__settings-item-value">{activeSubtitle >= 0 ? subtitles[activeSubtitle]?.displayTitle : 'Off'}</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                          </button>
                        )}
                      </>
                    )}
                    {settingsTab === 'speed' && (
                      <div className="vp__settings-options">
                        {PLAYBACK_SPEEDS.map(speed => (
                          <button
                            key={speed}
                            className={`vp__settings-option ${playbackSpeed === speed ? 'vp__settings-option--active' : ''}`}
                            onClick={() => { setPlaybackSpeed(speed); if (videoRef.current) videoRef.current.playbackRate = speed; setShowSettings(false); setSettingsTab('main'); }}
                          >
                            <span>{speed}x</span>
                            {speed === 1 && <span className="vp__settings-option-tag">Normal</span>}
                            {playbackSpeed === speed && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          </button>
                        ))}
                      </div>
                    )}
                    {settingsTab === 'quality' && (
                      <div className="vp__settings-options">
                        {QUALITY_OPTIONS.map(q => (
                          <button
                            key={q.label}
                            className={`vp__settings-option ${quality.label === q.label ? 'vp__settings-option--active' : ''}`}
                            onClick={() => { setQuality(q); setShowSettings(false); setSettingsTab('main'); }}
                          >
                            <span>{q.label}</span>
                            {quality.label === q.label && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          </button>
                        ))}
                      </div>
                    )}
                    {settingsTab === 'subtitles' && (
                      <div className="vp__settings-options">
                        <button
                          className={`vp__settings-option ${activeSubtitle === -1 ? 'vp__settings-option--active' : ''}`}
                          onClick={() => { setActiveSubtitle(-1); setShowSettings(false); setSettingsTab('main'); }}
                        >
                          <span>Off</span>
                          {activeSubtitle === -1 && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                        </button>
                        {subtitles.map(sub => (
                          <button
                            key={sub.index}
                            className={`vp__settings-option ${activeSubtitle === sub.index ? 'vp__settings-option--active' : ''}`}
                            onClick={() => { setActiveSubtitle(sub.index); setShowSettings(false); setSettingsTab('main'); }}
                          >
                            <span>{sub.displayTitle}</span>
                            {activeSubtitle === sub.index && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Picture in Picture */}
            {document.pictureInPictureEnabled && (
              <button className={`vp__ctrl-btn ${isPiP ? 'vp__ctrl-btn--active' : ''}`} onClick={togglePiP} data-tooltip="Picture in Picture">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor"/></svg>
              </button>
            )}

            {/* Fullscreen */}
            <button className="vp__ctrl-btn" onClick={toggleFullscreen} data-tooltip={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              {isFullscreen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard Hints */}
      <div className="vp__hints">
        <kbd>Space</kbd> Play/Pause
        <kbd>←</kbd><kbd>→</kbd> Seek
        <kbd>F</kbd> Fullscreen
        <span>,/.: Speed</span>
      </div>
    </div>
  );
};

// Alphabetical Index for fast scrolling
const AlphabeticalIndex: React.FC<{ items: MediaItem[]; onLetterClick: (letter: string) => void }> = ({ items, onLetterClick }) => {
  const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Get available letters based on items
  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    items.forEach(item => {
      const firstChar = item.title.charAt(0).toUpperCase();
      if (/[A-Z]/.test(firstChar)) {
        letters.add(firstChar);
      } else {
        letters.add('#');
      }
    });
    return letters;
  }, [items]);

  return (
    <div className="alpha-index">
      {alphabet.map(letter => (
        <button
          key={letter}
          className={`alpha-index__letter ${availableLetters.has(letter) ? '' : 'alpha-index__letter--dim'}`}
          onClick={() => onLetterClick(letter)}
          style={{ opacity: availableLetters.has(letter) ? 1 : 0.3 }}
        >
          {letter}
        </button>
      ))}
    </div>
  );
};

const MobileNav: React.FC<{ activeNav: string; onNavChange: (id: string) => void; onSearchClick: () => void }> = ({ activeNav, onNavChange, onSearchClick }) => (
  <nav className="orama-mobile-nav">
    {[{ id: 'home', label: 'Home', icon: Icons.home }, { id: 'discover', label: 'Discover', icon: Icons.discover }, { id: 'watchlist', label: 'Watchlist', icon: Icons.watchlist }].map(item => (
      <button key={item.id} className={`orama-mobile-nav__item${activeNav === item.id ? ' orama-mobile-nav__item--active' : ''}`} onClick={() => onNavChange(item.id)}>{item.icon}<span>{item.label}</span></button>
    ))}
    <button className="orama-mobile-nav__item" onClick={onSearchClick}>{Icons.search}<span>Search</span></button>
  </nav>
);

// ========== MAIN ==========
export default function MediaPage() {
  const [activeNav, setActiveNav] = useState('home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Sidebar starts collapsed
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [playingItem, setPlayingItem] = useState<MediaItem | null>(null);
  const [similarItems, setSimilarItems] = useState<MediaItem[]>([]);
  const [featuredItem, setFeaturedItem] = useState<MediaItem | null>(null);
  const [allMovies, setAllMovies] = useState<MediaItem[]>([]);
  const [allShows, setAllShows] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Toggle sidebar
  const toggleSidebar = useCallback(() => setSidebarCollapsed(prev => !prev), []);

  // Extract all unique actors from the library
  const allActors = useMemo(() => {
    const actorSet = new Set<string>();
    [...allMovies, ...allShows].forEach(item => {
      item.actors?.forEach(a => actorSet.add(a));
    });
    return Array.from(actorSet).sort();
  }, [allMovies, allShows]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.genres.length > 0) count++;
    if (filters.duration !== DURATION_FILTERS[0]) count++;
    if (filters.year !== YEAR_FILTERS[0]) count++;
    if (filters.rating !== RATING_FILTERS[0]) count++;
    if (filters.contentType !== 'all') count++;
    if (filters.minRating > 0) count++;
    if (filters.actors.length > 0) count++;
    if (filters.sort !== SORT_OPTIONS[0]) count++;
    return count;
  }, [filters]);

  const toggleSection = (section: string) => {
    console.log(`[MediaPage] Toggling section "${section}"`, { current: expandedSections[section], newValue: !expandedSections[section] });
    setExpandedSections(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      console.log(`[MediaPage] Section state updated:`, newState);
      return newState;
    });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        // Optimized fields - removed MediaStreams and Path for faster initial load
        const fields = 'Overview,Genres,CommunityRating,CriticRating,People,Studios,ProviderIds,OfficialRating,Taglines,RunTimeTicks';
        // Fetch up to 2000 items for better coverage while maintaining performance
        const [moviesRes, showsRes] = await Promise.all([
          fetch(`${EMBY_CONFIG.serverUrl}/emby/Items?IncludeItemTypes=Movie&Recursive=true&SortBy=DateCreated&SortOrder=Descending&Fields=${fields}&IsFolder=false&Limit=2000&api_key=${EMBY_CONFIG.apiKey}`),
          fetch(`${EMBY_CONFIG.serverUrl}/emby/Items?IncludeItemTypes=Series&Recursive=true&SortBy=DateCreated&SortOrder=Descending&Fields=${fields}&Limit=1000&api_key=${EMBY_CONFIG.apiKey}`)
        ]);
        if (moviesRes.ok) {
          const data = await moviesRes.json();
          console.log(`[MediaPage] Loaded ${data.Items?.length || 0} movies of ${data.TotalRecordCount} total`);
          // Map to MediaItem and group versions of the same movie
          const rawMovies = (data.Items || [])
            .filter((item: any) => !item.IsFolder && item.Type === 'Movie') // Extra filter for video files only
            .map(embyToMediaItem);
          const groupedMovies = groupMediaVersions(rawMovies);
          console.log(`[MediaPage] Grouped ${rawMovies.length} movies into ${groupedMovies.length} unique titles`);
          setAllMovies(groupedMovies);
          const featured = groupedMovies.find((m: MediaItem) => m.backdropUrl) || groupedMovies[0];
          if (featured) setFeaturedItem(featured);
        }
        if (showsRes.ok) {
          const data = await showsRes.json();
          console.log(`[MediaPage] Loaded ${data.Items?.length || 0} TV shows of ${data.TotalRecordCount} total`);
          // For series, also group versions
          const rawShows = (data.Items || [])
            .filter((item: any) => item.Type === 'Series')
            .map(embyToMediaItem);
          const groupedShows = groupMediaVersions(rawShows);
          console.log(`[MediaPage] Grouped ${rawShows.length} shows into ${groupedShows.length} unique titles`);
          setAllShows(groupedShows);
        }
      } catch (err) { console.error('Fetch error:', err); }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Comprehensive filtering function
  const applyFilters = useCallback((items: MediaItem[]): MediaItem[] => {
    let result = [...items];

    // Content type filter
    if (filters.contentType !== 'all') {
      result = result.filter(i => i.type === filters.contentType);
    }

    // Genre filter (match any selected genre)
    if (filters.genres.length > 0) {
      result = result.filter(i =>
        i.genres.some(g =>
          filters.genres.some(fg =>
            g.toLowerCase().includes(fg.toLowerCase()) ||
            fg.toLowerCase().includes(g.toLowerCase())
          )
        )
      );
    }

    // Duration filter
    if (filters.duration !== DURATION_FILTERS[0]) {
      result = result.filter(i => {
        const mins = getRuntimeMinutes(i.runtime);
        if (mins === 0) return true; // Include items without runtime data
        return mins >= filters.duration.min && mins < filters.duration.max;
      });
    }

    // Year filter
    if (filters.year !== YEAR_FILTERS[0]) {
      result = result.filter(i => {
        if (!i.year) return true;
        return i.year >= filters.year.min && i.year <= filters.year.max;
      });
    }

    // MPAA Rating filter
    if (filters.rating !== RATING_FILTERS[0] && filters.rating.values.length > 0) {
      result = result.filter(i => {
        if (!i.officialRating) return filters.rating.values.includes('');
        return filters.rating.values.some(r =>
          i.officialRating?.toLowerCase() === r.toLowerCase()
        );
      });
    }

    // Minimum score filter
    if (filters.minRating > 0) {
      result = result.filter(i => (i.rating || 0) >= filters.minRating);
    }

    // Actor filter (match all selected actors)
    if (filters.actors.length > 0) {
      result = result.filter(i =>
        filters.actors.every(actor =>
          i.actors?.some(a => a.toLowerCase().includes(actor.toLowerCase()))
        )
      );
    }

    // Sort
    result.sort((a, b) => {
      const { key, order } = filters.sort;
      let cmp = 0;
      switch (key) {
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'year':
          cmp = (a.year || 0) - (b.year || 0);
          break;
        case 'rating':
          cmp = (a.rating || 0) - (b.rating || 0);
          break;
        default:
          cmp = 0;
      }
      return order === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [filters]);

  // Get ALL top rated items (no limit) - sorted by rating descending
  const getTopRated = useCallback((items: MediaItem[]) => [...items].filter(i => i.rating).sort((a, b) => (b.rating || 0) - (a.rating || 0)), []);

  const handleSearch = useCallback(async (query: string) => {
    try {
      const res = await fetch(`${EMBY_CONFIG.serverUrl}/emby/Items?SearchTerm=${encodeURIComponent(query)}&Limit=30&Recursive=true&IncludeItemTypes=Movie,Series&Fields=Overview,Genres,CommunityRating,CriticRating,People,Studios&api_key=${EMBY_CONFIG.apiKey}`);
      if (res.ok) { const data = await res.json(); setSearchResults((data.Items || []).map(embyToMediaItem)); }
    } catch (err) { console.error('Search error:', err); }
  }, []);

  useEffect(() => {
    if (!selectedItem) { setSimilarItems([]); return; }
    const genre = selectedItem.genres[0];
    if (genre) setSimilarItems([...allMovies, ...allShows].filter(i => i.id !== selectedItem.id && i.genres.includes(genre)).slice(0, 8));
  }, [selectedItem, allMovies, allShows]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
      if (e.key === 'Escape') { setSearchOpen(false); setSelectedItem(null); setPlayingItem(null); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Apply filters to content
  const filteredMovies = useMemo(() => applyFilters(allMovies), [allMovies, applyFilters]);
  const filteredShows = useMemo(() => applyFilters(allShows), [allShows, applyFilters]);
  const allFiltered = useMemo(() => applyFilters([...allMovies, ...allShows]), [allMovies, allShows, applyFilters]);
  const continueWatching = [...allMovies, ...allShows].slice(0, 8);
  const topRated = getTopRated(allFiltered);

  // Handle filter reset
  const handleResetFilters = () => setFilters(defaultFilters);

  if (loading) return (
    <div className="media-app">
      <Sidebar activeNav={activeNav} onNavChange={setActiveNav} isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <main className="media-main">
        <div className="media-loading">
          <div className="media-spinner" />
          <p>Loading library...</p>
        </div>
      </main>
    </div>
  );

  return (
    <div className="media-app">
      <Sidebar activeNav={activeNav} onNavChange={setActiveNav} isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <main className={`media-main ${sidebarCollapsed ? '' : 'media-main--sidebar-open'}`}>
        {/* Logo Branding */}
        <div className="media-branding">
          <img
            src="https://jalenward.com/logo.png"
            alt="ALL STAR MEDIA PRO"
            className="media-branding__image"
          />
        </div>

        <Header
          filters={filters}
          onFiltersChange={setFilters}
          onSearchClick={() => setSearchOpen(true)}
          allActors={allActors}
          onResetFilters={handleResetFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {/* Advanced Filter Panel */}
        <FilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          allActors={allActors}
          onReset={handleResetFilters}
          activeFiltersCount={activeFiltersCount}
        />

        <Hero item={featuredItem} onPlay={() => featuredItem && setPlayingItem(featuredItem)} onAddWatchlist={() => {}} />

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && (
          <div className="orama-active-filters">
            <span className="orama-active-filters__label">Active Filters:</span>
            {filters.genres.length > 0 && <span className="orama-active-filters__tag">Genres: {filters.genres.join(', ')}</span>}
            {filters.contentType !== 'all' && <span className="orama-active-filters__tag">Type: {filters.contentType === 'movie' ? 'Movies' : 'TV Shows'}</span>}
            {filters.duration !== DURATION_FILTERS[0] && <span className="orama-active-filters__tag">Duration: {filters.duration.label}</span>}
            {filters.year !== YEAR_FILTERS[0] && <span className="orama-active-filters__tag">Year: {filters.year.label}</span>}
            {filters.rating !== RATING_FILTERS[0] && <span className="orama-active-filters__tag">Rating: {filters.rating.label}</span>}
            {filters.minRating > 0 && <span className="orama-active-filters__tag">Min Score: {filters.minRating}+</span>}
            {filters.actors.length > 0 && <span className="orama-active-filters__tag">Actors: {filters.actors.join(', ')}</span>}
            <span className="orama-active-filters__results">{allFiltered.length} results</span>
          </div>
        )}

        {/* Continue Watching - keeps original format */}
        <section className="orama-section">
          <div className="orama-section__header">
            <h2 className="orama-section__title">Continue Watching<span className="orama-section__count"> ({continueWatching.length})</span></h2>
          </div>
          <div className="orama-row">{continueWatching.map(item => <ContinueCard key={item.id} item={item} onClick={() => setSelectedItem(item)} />)}</div>
        </section>

        {/* Show "All Results" when filters are active */}
        {activeFiltersCount > 0 ? (
          <ContentSection
            title="Filtered Results"
            count={allFiltered.length}
            items={allFiltered}
            onItemClick={setSelectedItem}
            expanded={expandedSections['filtered']}
            onToggleExpand={() => toggleSection('filtered')}
          />
        ) : (
          <>
            <ContentSection title="Trending Now" count={topRated.length} items={topRated} onItemClick={setSelectedItem} expanded={expandedSections['trending']} onToggleExpand={() => toggleSection('trending')} />
            <ContentSection title="Movies" count={filteredMovies.length} items={filteredMovies} onItemClick={setSelectedItem} expanded={expandedSections['movies']} onToggleExpand={() => toggleSection('movies')} />
            <ContentSection title="TV Shows" count={filteredShows.length} items={filteredShows} onItemClick={setSelectedItem} expanded={expandedSections['shows']} onToggleExpand={() => toggleSection('shows')} />
          </>
        )}
      </main>

      {/* Alphabetical Index for quick navigation */}
      <AlphabeticalIndex
        items={allFiltered}
        onLetterClick={(letter) => {
          // Find first item starting with this letter and scroll to section
          const targetItem = allFiltered.find(item => {
            const firstChar = item.title.charAt(0).toUpperCase();
            if (letter === '#') return !/[A-Z]/.test(firstChar);
            return firstChar === letter;
          });
          if (targetItem) {
            // Scroll to the item - expand movies section and find the element
            setExpandedSections(prev => ({ ...prev, movies: true, shows: true, trending: true }));
            setTimeout(() => {
              const element = document.querySelector(`[data-title="${targetItem.title}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
        }}
      />

      <MobileNav activeNav={activeNav} onNavChange={setActiveNav} onSearchClick={() => setSearchOpen(true)} />
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} results={searchResults} onSearch={handleSearch} onItemClick={setSelectedItem} />
      <DetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onPlay={(versionId) => {
        if (selectedItem) {
          // If a specific version is selected, create a modified item with that version's ID
          const itemToPlay = versionId && selectedItem.id !== versionId
            ? { ...selectedItem, id: versionId }
            : selectedItem;
          setPlayingItem(itemToPlay);
          setSelectedItem(null);
        }
      }} similarItems={similarItems} onSelectSimilar={setSelectedItem} onActorClick={(actorName) => {
        // Close the modal and filter by this actor
        setSelectedItem(null);
        setFilters(prev => ({ ...prev, actors: [actorName] }));
        setActiveNav('discover'); // Switch to discover view to show filtered results
      }} />
      <VideoPlayer item={playingItem} onClose={() => setPlayingItem(null)} />
    </div>
  );
}
